import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import {
  Send, Plus, Search, Eye, Pencil, Trash2, Loader2, Clock,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, Upload, X,
  FileDown, BarChart3, Building2, Timer, ChevronRight, FileText, Calendar, Paperclip, ClipboardCheck, Package
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ClientTypeBadge, ClientTypeCounters, getClientType, NATURES_PROJET } from '../components/dossiers/ClientTypeBadge';
import { MaterielRecapTab } from '../components/dossiers/MaterielRecapTab';

const API = process.env.REACT_APP_BACKEND_URL;

const ENSEIGNES = ['Leclerc', 'Intermarché', 'Système U', 'Carrefour', 'Auchan'];

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon', cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText },
  envoye: { label: 'Envoyé', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: Send },
  relance_1: { label: 'Relance 1', cls: 'bg-orange-50 text-orange-700 border-orange-200', icon: Timer },
  relance_2: { label: 'Relance 2', cls: 'bg-red-50 text-red-700 border-red-200 animate-pulse', icon: AlertTriangle },
  valide_agenceur: { label: 'Validé', cls: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2 },
  refuse_agenceur: { label: 'Refusé', cls: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  date_modifiee: { label: 'Date modifiée', cls: 'bg-amber-50 text-amber-700 border-amber-200 animate-pulse', icon: Clock },
};

const SLA_ICON = ({ respected, status }) => {
  if (respected === true) return <CheckCircle2 className="w-4 h-4 text-green-600" />;
  if (respected === false) return <XCircle className="w-4 h-4 text-red-600" />;
  if (['relance_2'].includes(status)) return <AlertTriangle className="w-4 h-4 text-red-500 animate-pulse" />;
  if (['envoye', 'relance_1'].includes(status)) return <Clock className="w-4 h-4 text-orange-500" />;
  return <span className="text-xs text-slate-400">—</span>;
};

export default function DossiersAgenceurPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterClientType, setFilterClientType] = useState('');
  const [activeTab, setActiveTab] = useState('commandes');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    enseigne: '', ville: '', adresse: '', code_postal: '', nature_projet: '',
    responsable_wasabi: '', date_previsionnelle: '', agenceur_email: '', agenceur_name: '',
    organisation_name: '', notes: '',
    cmd_inox: '', cmd_inox_detail: '', cmd_inox_comment: '',
    cmd_vitrines: '', cmd_vitrines_detail: '', cmd_vitrines_comment: '',
    cmd_corner: '', cmd_corner_detail: '', cmd_corner_comment: '',
    demontage_ancien: '', besoin_demontage: '', demontage_meme_jour: '', date_demontage: '',
    remplace_corner: '', prevoir_demontage: '', concurrent_demontage: '',
    labo_equiper: '', bac_refrigere: '', demontage_corner: '', demontage_corner_type: '',
    evacuation_corner: '', evacuation_corner_type: '',
  });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewDossier, setViewDossier] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [appUsers, setAppUsers] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [agenceursList, setAgenceursList] = useState([]);
  const [showCalendar, setShowCalendar] = useState(true);
  const [showDateChange, setShowDateChange] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [dateReason, setDateReason] = useState('');
  const [changingDate, setChangingDate] = useState(false);
  const [previewDossier, setPreviewDossier] = useState(null); // { id, html, subject, to, files }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const [dRes, sRes, uRes, aRes] = await Promise.all([
        fetch(`${API}/api/dossiers-agenceur`, { headers }),
        fetch(`${API}/api/dossiers-agenceur/stats`, { headers }),
        fetch(`${API}/api/users/list`, { headers }),
        fetch(`${API}/api/agenceurs-config`, { headers }),
      ]);
      if (dRes.ok) setDossiers(await dRes.json());
      if (sRes.ok) setStats(await sRes.json());
      if (uRes.ok) setAppUsers(await uRes.json());
      if (aRes.ok) setAgenceursList(await aRes.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Default date = today + 8 weeks
  const getDefault8Weeks = () => {
    const d = new Date();
    d.setDate(d.getDate() + 56);
    return d.toISOString().split('T')[0];
  };

  const resetForm = () => {
    setForm({ enseigne: '', ville: '', adresse: '', code_postal: '', responsable_wasabi: '', date_previsionnelle: getDefault8Weeks(), agenceur_email: '', agenceur_name: '', agenceur_name_custom: '', organisation_name: '', notes: '', nom_dossier: '', cmd_inox: '', cmd_inox_detail: '', cmd_inox_comment: '', cmd_vitrines: '', cmd_vitrines_detail: '', cmd_vitrines_comment: '', cmd_corner: '', cmd_corner_detail: '', cmd_corner_comment: '', labo_equiper: '', bac_refrigere: '', demontage_corner: '', demontage_corner_type: '', evacuation_corner: '', evacuation_corner_type: '' });
    setEditId(null);
  };

  // Check if date is less than 8 weeks from today
  const isDateTooSoon = (dateStr) => {
    if (!dateStr) return false;
    const target = new Date(dateStr);
    const minDate = new Date();
    minDate.setDate(minDate.getDate() + 56);
    return target < minDate;
  };

  // Auto-compute nom_dossier = Enseigne + Ville
  const updateNomDossier = (enseigne, ville) => {
    const nom = [enseigne, ville].filter(Boolean).join(' ');
    setForm(p => ({ ...p, nom_dossier: nom }));
  };

  const handleEnseigneChange = (val) => {
    setForm(p => ({ ...p, enseigne: val }));
    updateNomDossier(val, form.ville);
  };

  const handleAddressSelect = (addr) => {
    setForm(p => {
      const newVille = addr.city || p.ville;
      return {
        ...p,
        adresse: addr.address || p.adresse,
        code_postal: addr.postal_code || p.code_postal,
        ville: newVille,
        nom_dossier: [p.enseigne, newVille].filter(Boolean).join(' '),
      };
    });
  };

  const handleAgenceurSelect = (val) => {
    if (val === '__autre__') {
      setForm(p => ({ ...p, agenceur_name: '', agenceur_name_custom: '__autre__', agenceur_email: '' }));
    } else {
      const found = agenceursList.find(a => a.name === val);
      setForm(p => ({ ...p, agenceur_name: val, agenceur_name_custom: '', agenceur_email: found?.email || p.agenceur_email }));
    }
  };

  const handleSave = async () => {
    if (!form.enseigne || !form.ville || !form.agenceur_email) {
      toast.error('Enseigne, ville et email agenceur requis');
      return;
    }
    // Plan obligatoire pour les nouvelles commandes
    if (!editId && !pendingFile) {
      toast.error('Le plan est obligatoire. Veuillez joindre un fichier avant de lancer la commande.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.agenceur_name_custom === '__autre__') {
        // "Autre" mode: agenceur_name is user-typed, email is user-typed
      }
      delete payload.agenceur_name_custom;
      delete payload.nom_dossier;
      const url = editId ? `${API}/api/dossiers-agenceur/${editId}` : `${API}/api/dossiers-agenceur`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      if (res.ok) {
        const result = await res.json();
        const dossierId = result.id || editId;
        // Upload pending file if any
        if (pendingFile && dossierId) {
          const fd = new FormData();
          fd.append('file', pendingFile);
          await fetch(`${API}/api/dossiers-agenceur/${dossierId}/files`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
          });
        }
        // Auto-send if new dossier (not edit) — show preview modal first
        if (!editId && dossierId) {
          setShowForm(false);
          resetForm();
          setPendingFile(null);
          await openEmailPreview(dossierId);
          load();
          return;
        } else {
          toast.success('Dossier modifié');
        }
        setShowForm(false);
        resetForm();
        setPendingFile(null);
        load();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setSaving(false); }
  };

  const handleChangeDate = async () => {
    if (!showDateChange || !newDate) return;
    setChangingDate(true);
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/${showDateChange.id}/change-date`, {
        method: 'PUT', headers,
        body: JSON.stringify({ new_date: newDate, reason: dateReason }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Date modifiée');
        setShowDateChange(null); setNewDate(''); setDateReason('');
        load();
      } else {
        toast.error(data.detail || 'Erreur lors de la modification');
      }
    } catch (e) {
      toast.error('Erreur réseau');
      console.error('Change date error:', e);
    }
    finally { setChangingDate(false); }
  };

  const openEmailPreview = async (id) => {
    setLoadingPreview(true);
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/${id}/email-preview`, { headers });
      if (res.ok) {
        const data = await res.json();
        setPreviewDossier({ id, ...data });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur aperçu');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setLoadingPreview(false); }
  };

  const confirmSendFromPreview = async () => {
    if (!previewDossier) return;
    setConfirmingSend(true);
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/${previewDossier.id}/send`, { method: 'POST', headers });
      if (res.ok) {
        toast.success('Commande envoyée à l\'agenceur');
        setPreviewDossier(null);
        load();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur envoi');
      }
    } catch { toast.error('Erreur réseau'); }
    finally { setConfirmingSend(false); }
  };

  const handleSend = async (id) => {
    // Open preview modal instead of sending directly
    await openEmailPreview(id);
  };

  const handleNatureChange = async (id, nature) => {
    if (!nature) return;
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/${id}/nature`, { method: 'PATCH', headers, body: JSON.stringify({ nature_projet: nature }) });
      if (res.ok) {
        toast.success('Nature du projet mise à jour');
        setViewDossier(v => (v && v.id === id ? { ...v, nature_projet: nature } : v));
        load();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur réseau'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/${deleteTarget.id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Supprimé'); setDeleteTarget(null); load(); }
      else { toast.error('Erreur'); }
    } catch { toast.error('Erreur réseau'); }
    finally { setDeleting(false); }
  };

  const handleFileUpload = async (e, dossierId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/dossiers-agenceur/${dossierId}/files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) { toast.success('Fichier ajouté'); load(); loadView(dossierId); }
      else { toast.error('Erreur upload'); }
    } catch { toast.error('Erreur réseau'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const loadView = async (id) => {
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/${id}`, { headers });
      if (res.ok) setViewDossier(await res.json());
    } catch { /* ignore */ }
  };

  const handleDownloadPdf = async (id, enseigne, ville) => {
    try {
      const res = await fetch(`${API}/api/dossiers-agenceur/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url;
        a.download = `Commande_Corner_${enseigne || 'dossier'}_${ville || ''}.pdf`.replace(/\s+/g, '_');
        a.click(); window.URL.revokeObjectURL(url);
      } else toast.error('Erreur lors du téléchargement PDF');
    } catch { toast.error('Erreur réseau'); }
  };

  const openEdit = (d) => {
    const isKnown = agenceursList.some(a => a.name === d.agenceur_name);
    setForm({
      enseigne: d.enseigne || '', ville: d.ville || '', adresse: d.adresse || '',
      code_postal: d.code_postal || '', nature_projet: d.nature_projet || '',
      responsable_wasabi: d.responsable_wasabi || '',
      date_previsionnelle: d.date_previsionnelle || '', agenceur_email: d.agenceur_email || '',
      agenceur_name: isKnown ? d.agenceur_name : (d.agenceur_name || ''),
      agenceur_name_custom: isKnown ? '' : '__autre__',
      organisation_name: d.organisation_name || '', notes: d.notes || '',
      nom_dossier: [d.enseigne, d.ville].filter(Boolean).join(' '),
      cmd_inox: d.cmd_inox || '', cmd_inox_detail: d.cmd_inox_detail || '', cmd_inox_comment: d.cmd_inox_comment || '',
      cmd_vitrines: d.cmd_vitrines || '', cmd_vitrines_detail: d.cmd_vitrines_detail || '', cmd_vitrines_comment: d.cmd_vitrines_comment || '',
      cmd_corner: d.cmd_corner || '', cmd_corner_detail: d.cmd_corner_detail || '', cmd_corner_comment: d.cmd_corner_comment || '',
      demontage_ancien: d.demontage_ancien || '', besoin_demontage: d.besoin_demontage || '',
      demontage_meme_jour: d.demontage_meme_jour || '', date_demontage: d.date_demontage || '',
      remplace_corner: d.remplace_corner || '', prevoir_demontage: d.prevoir_demontage || '',
      concurrent_demontage: d.concurrent_demontage || '',
      labo_equiper: d.labo_equiper || '', bac_refrigere: d.bac_refrigere || '',
      demontage_corner: d.demontage_corner || '', demontage_corner_type: d.demontage_corner_type || '',
      evacuation_corner: d.evacuation_corner || '', evacuation_corner_type: d.evacuation_corner_type || '',
    });
    setEditId(d.id);
    setShowForm(true);
  };

  const filtered = dossiers.filter(d => {
    const q = search.toLowerCase();
    const matchSearch = !q || [d.enseigne, d.ville, d.agenceur_name, d.agenceur_email, d.responsable_wasabi].some(f => (f || '').toLowerCase().includes(q));
    const matchStatus = !filterStatus || d.status === filterStatus;
    const matchType = !filterClientType || getClientType(d.nature_projet) === filterClientType;
    return matchSearch && matchStatus && matchType;
  });

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return '-'; } };
  const fmtDateTime = (d) => { try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; } };
  const fmtHours = (h) => h != null ? `${h}h` : '-';

  if (loading) return <AppLayout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4 sm:space-y-6" data-testid="dossiers-agenceur-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Send className="w-6 h-6 text-orange-500" /> Commande Corner
            </h1>
            <p className="text-sm text-slate-500 mt-1">Gestion des commandes et suivi SLA</p>
          </div>
          <div className="flex items-center gap-2 sm:flex-shrink-0">
            <Button onClick={() => navigate('/pv-livraison')}
              variant="outline"
              className="gap-1 flex-1 sm:flex-none border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="open-pv-timeline-btn"
              title="Timeline des PV de livraison"
            ><ClipboardCheck className="w-4 h-4" /><span className="hidden sm:inline">Timeline PV</span><span className="sm:hidden">PV</span></Button>
            {activeTab === 'commandes' && <Button onClick={() => setShowCalendar(!showCalendar)}
              variant={showCalendar ? 'default' : 'outline'}
              className="gap-1 flex-1 sm:flex-none" data-testid="toggle-calendar-btn"
            ><Calendar className="w-4 h-4" />{showCalendar ? 'Liste' : 'Calendrier'}</Button>}
            {isAdmin && <Button onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-orange-500 hover:bg-orange-600 text-white gap-1 flex-1 sm:flex-none" data-testid="new-dossier-btn"
            ><Plus className="w-4 h-4" /><span className="hidden sm:inline">Nouveau dossier</span><span className="sm:hidden">Nouveau</span></Button>}
          </div>
        </div>

        {isAdmin && (
          <div className="flex gap-1 border-b border-slate-200" data-testid="dossiers-tabs">
            {[{ k: 'commandes', l: 'Commandes', I: Send }, { k: 'recap', l: 'Récapitulatif matériel à commander', I: Package }].map(({ k, l, I }) => (
              <button key={k} type="button" onClick={() => setActiveTab(k)} data-testid={`tab-${k}`}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${activeTab === k ? 'border-orange-500 text-orange-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                <I className="w-4 h-4" />{l}
              </button>
            ))}
          </div>
        )}

        {isAdmin && activeTab === 'recap' ? <MaterielRecapTab token={token} users={appUsers.filter(u => u.role !== 'prestataire')} /> : (<>
        {/* KPIs - mobile-readable layout */}
        {stats && (
          <>
            {/* Mobile: 3 main KPIs in a row, secondary in collapsible */}
            <div className="grid grid-cols-3 gap-2 sm:hidden" data-testid="dossier-kpis-mobile">
              <Card className="border-slate-200"><CardContent className="p-2 text-center">
                <p className="text-xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-[10px] text-slate-500">Total</p>
              </CardContent></Card>
              <Card className="border-blue-200"><CardContent className="p-2 text-center">
                <p className="text-xl font-bold text-blue-600">{stats.en_attente}</p>
                <p className="text-[10px] text-slate-500">En attente</p>
              </CardContent></Card>
              <Card className="border-emerald-200"><CardContent className="p-2 text-center">
                <p className="text-xl font-bold text-emerald-600">{stats.sla_respected_pct}%</p>
                <p className="text-[10px] text-slate-500">SLA OK</p>
              </CardContent></Card>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:hidden">
              <Card className="border-green-200"><CardContent className="p-2 text-center">
                <p className="text-base font-bold text-green-600">{stats.taux_validation}%</p>
                <p className="text-[10px] text-slate-500">Validation</p>
              </CardContent></Card>
              <Card className="border-red-200"><CardContent className="p-2 text-center">
                <p className="text-base font-bold text-red-600">{stats.taux_refus}%</p>
                <p className="text-[10px] text-slate-500">Refus</p>
              </CardContent></Card>
              <Card className="border-orange-200"><CardContent className="p-2 text-center">
                <p className="text-base font-bold text-orange-600">{fmtHours(stats.delai_moyen_heures)}</p>
                <p className="text-[10px] text-slate-500">Délai moy.</p>
              </CardContent></Card>
            </div>
            {/* Desktop: full 6-card row */}
            <div className="hidden sm:grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="dossier-kpis">
              <Card className="border-slate-200"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </CardContent></Card>
              <Card className="border-blue-200"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.en_attente}</p>
                <p className="text-xs text-slate-500">En attente</p>
              </CardContent></Card>
              <Card className="border-green-200"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.taux_validation}%</p>
                <p className="text-xs text-slate-500">Validation</p>
              </CardContent></Card>
              <Card className="border-red-200"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.taux_refus}%</p>
                <p className="text-xs text-slate-500">Refus</p>
              </CardContent></Card>
              <Card className="border-orange-200"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-orange-600">{fmtHours(stats.delai_moyen_heures)}</p>
                <p className="text-xs text-slate-500">Délai moyen</p>
              </CardContent></Card>
              <Card className="border-emerald-200"><CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600">{stats.sla_respected_pct}%</p>
                <p className="text-xs text-slate-500">SLA respecté</p>
              </CardContent></Card>
            </div>
          </>
        )}

        {/* Filters - mobile-first */}
        <div className="space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-3">
          <div className="relative sm:flex-1 sm:max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Rechercher enseigne, ville, agenceur…" value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9" data-testid="search-dossiers" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="w-full sm:w-auto border rounded-lg px-3 py-2 text-sm bg-white h-10" data-testid="filter-status">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <ClientTypeCounters dossiers={dossiers} value={filterClientType} onChange={setFilterClientType} />
        {(search || filterStatus || filterClientType) && (
          <p className="text-xs text-slate-500 -mt-2" data-testid="dossier-result-count">
            {filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </p>
        )}

        {/* Calendar View */}
        {showCalendar && (() => {
          const validatedDossiers = dossiers.filter(d => d.date_previsionnelle && d.status !== 'brouillon'
            && (!filterClientType || getClientType(d.nature_projet) === filterClientType));
          const byMonth = {};
          validatedDossiers.forEach(d => {
            const date = new Date(d.date_previsionnelle);
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
            if (!byMonth[key]) byMonth[key] = { label, items: [] };
            byMonth[key].items.push(d);
          });
          const sortedMonths = Object.keys(byMonth).sort();
          
          return (
            <Card className="border-blue-200 bg-blue-50/30" data-testid="calendar-view">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  Calendrier des installations ({validatedDossiers.length} chantiers à venir)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sortedMonths.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">Aucun chantier validé avec date prévisionnelle</p>
                ) : (
                  <div className="space-y-4">
                    {sortedMonths.map(monthKey => {
                      const month = byMonth[monthKey];
                      const sorted = month.items.sort((a, b) => new Date(a.date_previsionnelle) - new Date(b.date_previsionnelle));
                      return (
                        <div key={monthKey}>
                          <h3 className="font-bold text-blue-800 text-sm mb-2 capitalize flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-blue-500" />
                            {month.label} <Badge variant="secondary" className="text-[10px]">{sorted.length}</Badge>
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {sorted.map(d => {
                              const day = new Date(d.date_previsionnelle).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
                              const fileName = d.files && d.files.length > 0 ? d.files[0].original_filename : '';
                              return (
                                <div key={d.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-blue-100 hover:shadow-sm cursor-pointer transition-shadow"
                                  onClick={() => { loadView(d.id); setViewDossier(d); }} data-testid={`cal-item-${d.id}`}>
                                  <div className="text-center bg-blue-100 rounded-lg p-2 min-w-[60px]">
                                    <p className="text-[10px] text-blue-500 uppercase">{day.split(' ')[0]}</p>
                                    <p className="text-lg font-bold text-blue-800">{new Date(d.date_previsionnelle).getDate()}</p>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2">
                                      <p className="font-semibold text-sm text-slate-800 truncate">{d.enseigne} — {d.ville}</p>
                                      <ClientTypeBadge nature={d.nature_projet} testId={`cal-client-type-${d.id}`} />
                                    </div>
                                    <p className="text-[11px] text-slate-500 truncate">{d.agenceur_name || d.agenceur_email}</p>
                                    {fileName && (
                                      <a href={`${API}/api/dossiers-agenceur/${d.id}/files/${d.files[0].id}/view`}
                                        target="_blank" rel="noopener noreferrer"
                                        className="text-[10px] text-blue-600 flex items-center gap-1 break-all hover:underline hover:text-blue-800"
                                        onClick={e => e.stopPropagation()}
                                      ><Paperclip className="w-2.5 h-2.5 flex-shrink-0" />{fileName}</a>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Dossiers List */}
        <div className="space-y-2" data-testid="dossiers-list">
          {filtered.length === 0 ? (
            <Card className="border-dashed"><CardContent className="p-8 text-center text-slate-400">
              <Send className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>Aucun dossier</p>
            </CardContent></Card>
          ) : filtered.map(d => {
            const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.brouillon;
            const Icon = cfg.icon;
            return (
              <Card key={d.id} className="border-slate-200 hover:shadow-sm transition-shadow cursor-pointer border-l-4 border-l-orange-300"
                onClick={() => { loadView(d.id); setViewDossier(d); }} data-testid={`dossier-row-${d.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Title row */}
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{d.enseigne} — {d.ville}</p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] sm:text-xs text-slate-500 mt-1">
                            {(d.agenceur_name || d.agenceur_email) && (
                              <span className="flex items-center gap-1 truncate max-w-full"><Send className="w-3 h-3 flex-shrink-0" />{d.agenceur_name || d.agenceur_email}</span>
                            )}
                            {d.responsable_wasabi && (
                              <span className="hidden sm:inline">Resp: {d.responsable_wasabi}</span>
                            )}
                            {d.date_previsionnelle && (
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3 flex-shrink-0" />{fmtDate(d.date_previsionnelle)}</span>
                            )}
                          </div>
                          {/* Mobile: SLA chip + relances inline */}
                          <div className="flex items-center gap-2 mt-1.5 sm:hidden text-[11px]" title="SLA / Délai">
                            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-slate-50 rounded">
                              <SLA_ICON respected={d.sla_respected} status={d.status} />
                              {d.delay_hours != null && <span className="text-slate-600">{fmtHours(d.delay_hours)}</span>}
                            </span>
                            {d.nb_relances > 0 && (
                              <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded font-medium">
                                {d.nb_relances} relance{d.nb_relances > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <ClientTypeBadge nature={d.nature_projet} testId={`row-client-type-${d.id}`} />
                        {/* Status badge top-right (always visible) */}
                        <Badge variant="outline" className={`${cfg.cls} gap-1 shrink-0 text-[10px] sm:text-xs px-1.5 py-0`}>
                          <Icon className="w-3 h-3" />{cfg.label}
                        </Badge>
                      </div>
                      {d.files && d.files.length > 0 && (
                        <div className="flex items-center gap-1 text-xs text-blue-600 font-medium mt-1.5" onClick={e => e.stopPropagation()}>
                          <Paperclip className="w-3 h-3 flex-shrink-0" />
                          <a href={`${API}/api/dossiers-agenceur/${d.id}/files/${d.files[0].id}/view`}
                            target="_blank" rel="noopener noreferrer"
                            className="break-all hover:underline hover:text-blue-800 truncate"
                            data-testid={`file-link-${d.id}`}
                          >{d.files[0].original_filename}</a>
                        </div>
                      )}
                    </div>
                    {/* Desktop right side: SLA + actions */}
                    <div className="hidden sm:flex items-center gap-2 shrink-0">
                      <div className="flex items-center gap-1 text-xs" title={d.sla_respected === true ? 'SLA respecté' : d.sla_respected === false ? 'SLA dépassé' : 'En attente'}>
                        <SLA_ICON respected={d.sla_respected} status={d.status} />
                        {d.delay_hours != null && <span className="text-slate-500">{fmtHours(d.delay_hours)}</span>}
                        {d.nb_relances > 0 && <span className="text-orange-600 font-medium">{d.nb_relances}x</span>}
                      </div>
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        {isAdmin && d.status === 'brouillon' && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                            onClick={() => handleSend(d.id)} disabled={sending === d.id} title="Envoyer"
                            data-testid={`send-dossier-${d.id}`}
                          >{sending === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
                        )}
                        {isAdmin && ['brouillon', 'refuse_agenceur'].includes(d.status) && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                            onClick={() => openEdit(d)} title="Modifier" data-testid={`edit-dossier-${d.id}`}
                          ><Pencil className="w-4 h-4" /></Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-50"
                          onClick={() => { loadView(d.id); setViewDossier(d); }} title="Voir"
                          data-testid={`view-dossier-${d.id}`}
                        ><Eye className="w-4 h-4" /></Button>
                        {isAdmin && d.date_previsionnelle && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600 hover:bg-amber-50"
                            onClick={() => { setShowDateChange(d); setNewDate(d.date_previsionnelle || ''); setDateReason(''); }}
                            title="Modifier la date" data-testid={`change-date-${d.id}`}
                          ><Calendar className="w-4 h-4" /></Button>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                          onClick={() => handleDownloadPdf(d.id, d.enseigne, d.ville)} title="Télécharger PDF"
                          data-testid={`pdf-dossier-${d.id}`}
                        ><FileDown className="w-4 h-4" /></Button>
                        {d.status === 'valide_agenceur' && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-indigo-600 hover:bg-indigo-50"
                            onClick={() => navigate(`/pv-livraison/${d.id}`)}
                            title="PV de livraison – Corner installé"
                            data-testid={`pv-livraison-${d.id}`}
                          ><ClipboardCheck className="w-4 h-4" /></Button>
                        )}
                        {isAdmin && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                            onClick={() => setDeleteTarget(d)} title="Supprimer"
                            data-testid={`delete-dossier-${d.id}`}
                          ><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                    {/* Mobile actions row (full-width with labels) */}
                    <div className="grid grid-cols-4 gap-1.5 sm:hidden pt-2 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                      <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-slate-600"
                        onClick={() => { loadView(d.id); setViewDossier(d); }}
                      ><Eye className="w-3.5 h-3.5" /> Voir</Button>
                      {isAdmin && d.status === 'brouillon' ? (
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-green-600 border-green-200"
                          onClick={() => handleSend(d.id)} disabled={sending === d.id}
                        >{sending === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5" /> Envoyer</>}</Button>
                      ) : isAdmin && ['brouillon', 'refuse_agenceur'].includes(d.status) ? (
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-blue-600 border-blue-200"
                          onClick={() => openEdit(d)}
                        ><Pencil className="w-3.5 h-3.5" /> Modif.</Button>
                      ) : isAdmin && d.date_previsionnelle ? (
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-amber-600 border-amber-200"
                          onClick={() => { setShowDateChange(d); setNewDate(d.date_previsionnelle || ''); setDateReason(''); }}
                        ><Calendar className="w-3.5 h-3.5" /> Date</Button>
                      ) : <span />}
                      <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-emerald-600 border-emerald-200"
                        onClick={() => handleDownloadPdf(d.id, d.enseigne, d.ville)}
                      ><FileDown className="w-3.5 h-3.5" /> PDF</Button>
                      {d.status === 'valide_agenceur' ? (
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-indigo-600 border-indigo-200"
                          onClick={() => navigate(`/pv-livraison/${d.id}`)}
                          data-testid={`pv-livraison-mobile-${d.id}`}
                        ><ClipboardCheck className="w-3.5 h-3.5" /> PV</Button>
                      ) : isAdmin ? (
                        <Button variant="outline" size="sm" className="h-9 px-1 text-[11px] gap-1 text-red-500 border-red-200"
                          onClick={() => setDeleteTarget(d)}
                        ><Trash2 className="w-3.5 h-3.5" /> Suppr.</Button>
                      ) : <span />}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        </>)}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) { setShowForm(false); resetForm(); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="dossier-form-modal">
          <DialogHeader>
            <DialogTitle className="text-orange-700">{editId ? 'Modifier le dossier' : 'Nouveau dossier agenceur'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Nom du dossier (auto) */}
            {form.nom_dossier && (
              <div className="p-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                <Label className="text-xs text-orange-600">Nom du dossier</Label>
                <p className="font-semibold text-slate-800" data-testid="nom-dossier">{form.nom_dossier}</p>
              </div>
            )}
            {/* Enseigne dropdown */}
            <div><Label className="text-xs">Enseigne *</Label>
              <select value={form.enseigne} onChange={e => handleEnseigneChange(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                data-testid="field-enseigne">
                <option value="">Sélectionner...</option>
                {ENSEIGNES.map(e => <option key={e} value={e}>{e}</option>)}
              </select></div>
            {/* Address autocomplete */}
            <div>
              <Label className="text-xs">Adresse (recherche Google Maps)</Label>
              <AddressAutocomplete onAddressSelect={handleAddressSelect} initialValue={form.adresse} label={false} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Code postal</Label>
                <Input value={form.code_postal} readOnly className="bg-slate-50" data-testid="field-cp" /></div>
              <div><Label className="text-xs">Ville</Label>
                <Input value={form.ville} readOnly className="bg-slate-50" data-testid="field-ville" /></div>
            </div>
            <div><Label className="text-xs">Nature du projet *</Label>
              <select value={form.nature_projet} onChange={e => setForm(p => ({ ...p, nature_projet: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                data-testid="field-nature-projet">
                <option value="">Sélectionner...</option>
                <option value="Nouveau corner">Nouveau corner</option>
                <option value="Remodeling">Remodeling</option>
                <option value="Relifting">Relifting</option>
                <option value="Matériel supplémentaire">Matériel supplémentaire</option>
              </select></div>

            {/* === Remplacement corner === */}
            <div className="space-y-3 p-3 border-l-4 border-amber-400 bg-amber-50/50 rounded-r-lg">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Cette commande remplace un corner</Label>
                <div className="flex gap-2 mt-1.5">
                  {['Wasabi', 'Concurrent', 'White space'].map(opt => (
                    <button key={opt} type="button"
                      onClick={() => setForm(p => ({ ...p, remplace_corner: opt, prevoir_demontage: '', concurrent_demontage: '', date_demontage: '' }))}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.remplace_corner === opt ? 'bg-amber-100 border-amber-400 text-amber-800 font-semibold' : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'}`}
                      data-testid={`field-remplace-corner-${opt.toLowerCase().replace(/\s/g, '-')}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Si Wasabi */}
              {form.remplace_corner === 'Wasabi' && (
                <div className="space-y-3 ml-2 pl-3 border-l-2 border-amber-300">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-600">Prévoir le démontage de notre ancien corner</Label>
                    <div className="flex gap-2">
                      {['Oui', 'Non'].map(opt => (
                        <button key={opt} type="button"
                          onClick={() => setForm(p => ({ ...p, prevoir_demontage: opt, ...(opt === 'Non' ? { date_demontage: '' } : {}) }))}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${form.prevoir_demontage === opt ? (opt === 'Oui' ? 'bg-emerald-100 border-emerald-400 text-emerald-700 font-semibold' : 'bg-red-100 border-red-400 text-red-700 font-semibold') : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'}`}
                          data-testid={`field-prevoir-demontage-${opt.toLowerCase()}`}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.prevoir_demontage === 'Oui' && (
                    <div>
                      <Label className="text-xs text-slate-600">Quelle est la date souhaitée ?</Label>
                      <Input type="date" value={form.date_demontage}
                        onChange={e => setForm(p => ({ ...p, date_demontage: e.target.value }))}
                        className="mt-1" data-testid="field-date-demontage" />
                    </div>
                  )}
                </div>
              )}

              {/* Si Concurrent */}
              {form.remplace_corner === 'Concurrent' && (
                <div className="ml-2 pl-3 border-l-2 border-amber-300">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-600">Le concurrent prévoit bien le démontage de son corner</Label>
                    <div className="flex gap-2">
                      {['Oui', 'Non'].map(opt => (
                        <button key={opt} type="button"
                          onClick={() => setForm(p => ({ ...p, concurrent_demontage: opt }))}
                          className={`px-3 py-1 text-xs rounded-full border transition-colors ${form.concurrent_demontage === opt ? (opt === 'Oui' ? 'bg-emerald-100 border-emerald-400 text-emerald-700 font-semibold' : 'bg-red-100 border-red-400 text-red-700 font-semibold') : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'}`}
                          data-testid={`field-concurrent-demontage-${opt.toLowerCase()}`}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* White space → pas de question */}
            </div>

            <div><Label className="text-xs">Responsable Wasabi</Label>
              <select value={form.responsable_wasabi} onChange={e => setForm(p => ({ ...p, responsable_wasabi: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                data-testid="field-responsable">
                <option value="">Sélectionner...</option>
                {appUsers.filter(u => u.role !== 'prestataire').map(u => (
                  <option key={u.id} value={u.name || u.email}>{u.name || u.email} ({u.role})</option>
                ))}
              </select></div>
            <div>
              <Label className="text-xs">Date prévisionnelle</Label>
              <Input type="date" value={form.date_previsionnelle} onChange={e => setForm(p => ({ ...p, date_previsionnelle: e.target.value }))} data-testid="field-date" />
              {isDateTooSoon(form.date_previsionnelle) && (
                <div className="flex items-center gap-1.5 mt-1.5 p-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="date-warning">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span className="text-xs text-amber-700">Attention : délai inférieur à 8 semaines. Risque sur les délais de mise en place.</span>
                </div>
              )}
            </div>
            {/* === Détails commande === */}
            <div className="border-t pt-3 mt-2">
              <p className="text-sm font-medium text-slate-700 mb-2">Détails de la commande</p>
              {/* Inox */}
              <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Label className="text-xs w-56">Commande des inox du visuel</Label>
                  <select value={form.cmd_inox} onChange={e => setForm(p => ({ ...p, cmd_inox: e.target.value, cmd_inox_detail: '', cmd_inox_comment: '' }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-cmd-inox">
                    <option value="">—</option><option value="Oui">Oui</option><option value="Non">Non</option>
                  </select>
                </div>
                {form.cmd_inox === 'Oui' && (
                  <div className="ml-4 space-y-2">
                    <select value={form.cmd_inox_detail} onChange={e => setForm(p => ({ ...p, cmd_inox_detail: e.target.value, cmd_inox_comment: '' }))}
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-cmd-inox-detail">
                      <option value="">Préciser...</option><option value="Intégralité">Intégralité</option><option value="Partiellement">Partiellement</option>
                    </select>
                    {form.cmd_inox_detail === 'Partiellement' && (
                      <Input placeholder="Précisez les éléments..." value={form.cmd_inox_comment} onChange={e => setForm(p => ({ ...p, cmd_inox_comment: e.target.value }))} data-testid="field-cmd-inox-comment" />
                    )}
                  </div>
                )}
              </div>
              {/* Vitrines */}
              <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Label className="text-xs w-56">Commande de(s) vitrine(s) du visuel</Label>
                  <select value={form.cmd_vitrines} onChange={e => setForm(p => ({ ...p, cmd_vitrines: e.target.value, cmd_vitrines_detail: '', cmd_vitrines_comment: '' }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-cmd-vitrines">
                    <option value="">—</option><option value="Oui">Oui</option><option value="Non">Non</option>
                  </select>
                </div>
                {form.cmd_vitrines === 'Oui' && (
                  <div className="ml-4 space-y-2">
                    <select value={form.cmd_vitrines_detail} onChange={e => setForm(p => ({ ...p, cmd_vitrines_detail: e.target.value, cmd_vitrines_comment: '' }))}
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-cmd-vitrines-detail">
                      <option value="">Préciser...</option><option value="Toutes">Toutes</option><option value="Partiellement">Partiellement</option>
                    </select>
                    {form.cmd_vitrines_detail === 'Partiellement' && (
                      <Input placeholder="Précisez les vitrines..." value={form.cmd_vitrines_comment} onChange={e => setForm(p => ({ ...p, cmd_vitrines_comment: e.target.value }))} data-testid="field-cmd-vitrines-comment" />
                    )}
                  </div>
                )}
              </div>
              {/* Corner */}
              <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Label className="text-xs w-56">Commande du corner (Périphérie et PLV)</Label>
                  <select value={form.cmd_corner} onChange={e => setForm(p => ({ ...p, cmd_corner: e.target.value, cmd_corner_detail: '', cmd_corner_comment: '' }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-cmd-corner">
                    <option value="">—</option><option value="Oui">Oui</option><option value="Non">Non</option>
                  </select>
                </div>
                {form.cmd_corner === 'Oui' && (
                  <div className="ml-4 space-y-2">
                    <select value={form.cmd_corner_detail} onChange={e => setForm(p => ({ ...p, cmd_corner_detail: e.target.value, cmd_corner_comment: '' }))}
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-cmd-corner-detail">
                      <option value="">Préciser...</option><option value="Intégralité">Intégralité</option><option value="Covering">Covering</option><option value="Autre">Autre</option>
                    </select>
                    {form.cmd_corner_detail === 'Autre' && (
                      <Input placeholder="Précisez..." value={form.cmd_corner_comment} onChange={e => setForm(p => ({ ...p, cmd_corner_comment: e.target.value }))} data-testid="field-cmd-corner-comment" />
                    )}
                  </div>
                )}
              </div>
              {/* Laboratoire à équiper */}
              <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Label className="text-xs w-56">Présence d'un laboratoire à équiper</Label>
                  <select value={form.labo_equiper} onChange={e => setForm(p => ({ ...p, labo_equiper: e.target.value }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-labo-equiper">
                    <option value="">—</option><option value="Oui">Oui</option><option value="Non">Non</option>
                  </select>
                </div>
              </div>
              {/* Bac réfrigéré */}
              <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Label className="text-xs w-56">Ajout d'un bac réfrigéré</Label>
                  <select value={form.bac_refrigere} onChange={e => setForm(p => ({ ...p, bac_refrigere: e.target.value }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-bac-refrigere">
                    <option value="">—</option><option value="Oui">Oui</option><option value="Non">Non</option>
                  </select>
                </div>
              </div>
              {/* Démontage corner à prévoir */}
              <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Label className="text-xs w-56">Démontage corner à prévoir</Label>
                  <select value={form.demontage_corner} onChange={e => setForm(p => ({ ...p, demontage_corner: e.target.value, demontage_corner_type: '' }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-demontage-corner">
                    <option value="">—</option><option value="Oui">Oui</option><option value="Non">Non</option>
                  </select>
                </div>
                {form.demontage_corner === 'Oui' && (
                  <div className="ml-4 space-y-2">
                    <select value={form.demontage_corner_type} onChange={e => setForm(p => ({ ...p, demontage_corner_type: e.target.value }))}
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-demontage-corner-type">
                      <option value="">Par qui ?</option><option value="Concurrent">Concurrent</option><option value="Wasabi">Wasabi</option>
                    </select>
                  </div>
                )}
              </div>
              {/* Évacuation du corner à prévoir */}
              <div className="space-y-2 mb-3 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Label className="text-xs w-56">Évacuation du corner à prévoir</Label>
                  <select value={form.evacuation_corner} onChange={e => setForm(p => ({ ...p, evacuation_corner: e.target.value, evacuation_corner_type: '' }))}
                    className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-evacuation-corner">
                    <option value="">—</option><option value="Oui">Oui</option><option value="Non">Non</option>
                  </select>
                </div>
                {form.evacuation_corner === 'Oui' && (
                  <div className="ml-4 space-y-2">
                    <select value={form.evacuation_corner_type} onChange={e => setForm(p => ({ ...p, evacuation_corner_type: e.target.value }))}
                      className="border rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="field-evacuation-corner-type">
                      <option value="">Par qui ?</option><option value="Wasabi">Wasabi</option><option value="DSAO">DSAO</option>
                    </select>
                  </div>
                )}
              </div>
            </div>
            <div className="border-t pt-3 mt-2">
              <p className="text-sm font-medium text-slate-700 mb-2">Agenceur destinataire</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Nom agenceur *</Label>
                  <select value={form.agenceur_name_custom === '__autre__' ? '__autre__' : form.agenceur_name} onChange={e => handleAgenceurSelect(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                    data-testid="field-agenceur-name">
                    <option value="">Sélectionner...</option>
                    {agenceursList.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                    <option value="__autre__">Autre (saisie libre)</option>
                  </select>
                  {form.agenceur_name_custom === '__autre__' && (
                    <Input className="mt-2" placeholder="Nom personnalisé" value={form.agenceur_name || ''} onChange={e => setForm(p => ({ ...p, agenceur_name: e.target.value }))} data-testid="field-agenceur-custom" />
                  )}</div>
                <div><Label className="text-xs">Email agenceur *</Label>
                  <Input type="email" value={form.agenceur_email} onChange={e => setForm(p => ({ ...p, agenceur_email: e.target.value }))} data-testid="field-agenceur-email" /></div>
              </div>
            </div>
            <div><Label className="text-xs">Notes / Remarques</Label>
              <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} data-testid="field-notes" /></div>
            {/* Upload plan - OBLIGATOIRE */}
            <div>
              <Label className="text-xs">Plan / Fichier à joindre <span className="text-red-500 font-bold">*</span></Label>
              <label className={`flex items-center gap-2 border-2 border-dashed rounded-lg p-3 cursor-pointer transition-colors mt-1 ${
                pendingFile ? 'border-green-300 bg-green-50' : 'border-orange-300 hover:border-orange-400 bg-orange-50/50'
              }`}>
                <Upload className={`w-4 h-4 ${pendingFile ? 'text-green-500' : 'text-orange-400'}`} />
                <span className={`text-sm ${pendingFile ? 'text-green-700 font-medium' : 'text-orange-600'}`}>
                  {pendingFile ? pendingFile.name : 'Cliquer pour joindre le plan (PDF, image...) — Obligatoire'}
                </span>
                {pendingFile && <X className="w-4 h-4 text-slate-400 ml-auto hover:text-red-500" onClick={e => { e.preventDefault(); setPendingFile(null); }} />}
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={e => setPendingFile(e.target.files?.[0] || null)} data-testid="field-upload-plan" />
              </label>
              {!pendingFile && !editId && (
                <p className="text-xs text-red-500 mt-1 flex items-center gap-1" data-testid="plan-required-warning">
                  <AlertTriangle className="w-3 h-3" /> Le plan est obligatoire pour lancer une commande
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => { setShowForm(false); resetForm(); }}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white gap-1" data-testid="save-dossier-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {editId ? 'Modifier' : 'Lancer la commande'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dossier Dialog */}
      <Dialog open={!!viewDossier} onOpenChange={v => { if (!v) setViewDossier(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-dossier-modal">
          {viewDossier && (() => {
            const cfg = STATUS_CONFIG[viewDossier.status] || STATUS_CONFIG.brouillon;
            const Icon = cfg.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-slate-800">
                    <Building2 className="w-5 h-5 text-orange-500" />
                    {viewDossier.enseigne} — {viewDossier.ville}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`${cfg.cls} gap-1`}><Icon className="w-3.5 h-3.5" />{cfg.label}</Badge>
                    <ClientTypeBadge nature={viewDossier.nature_projet} testId="view-client-type" />
                    {viewDossier.sla_respected === true && <Badge className="bg-green-100 text-green-700">SLA respecté</Badge>}
                    {viewDossier.sla_respected === false && <Badge className="bg-red-100 text-red-700">SLA dépassé</Badge>}
                    {viewDossier.nb_relances > 0 && <Badge className="bg-orange-100 text-orange-700">{viewDossier.nb_relances} relance(s)</Badge>}
                    {viewDossier.delay_hours != null && <Badge variant="outline">{fmtHours(viewDossier.delay_hours)} de délai</Badge>}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Adresse</span><p className="font-medium">{viewDossier.adresse} {viewDossier.code_postal}</p></div>
                    <div><span className="text-slate-500">Responsable Wasabi</span><p className="font-medium">{viewDossier.responsable_wasabi || '-'}</p></div>
                    <div><span className="text-slate-500">Date prévisionnelle</span><p className="font-medium">{viewDossier.date_previsionnelle ? fmtDate(viewDossier.date_previsionnelle) : '-'}</p></div>
                    <div><span className="text-slate-500">Agenceur</span><p className="font-medium">{viewDossier.agenceur_name} ({viewDossier.agenceur_email})</p></div>
                    <div><span className="text-slate-500">Nature du projet</span>
                      {isAdmin ? (
                        <select value={viewDossier.nature_projet || ''} onChange={e => handleNatureChange(viewDossier.id, e.target.value)}
                          className="w-full border rounded-lg px-2 py-1 text-sm bg-white mt-0.5" data-testid="view-nature-select">
                          <option value="">Non renseigné</option>
                          {NATURES_PROJET.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      ) : <p className="font-medium">{viewDossier.nature_projet || 'Non renseigné'}</p>}
                    </div>
                  </div>

                  {/* Remplacement corner */}
                  {viewDossier.remplace_corner && (
                    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-slate-500">Remplace un corner :</span>
                        <Badge variant="outline" className="font-semibold">{viewDossier.remplace_corner}</Badge>
                      </div>
                      {viewDossier.remplace_corner === 'Wasabi' && (
                        <div className="ml-4 space-y-1 text-xs text-slate-600">
                          {viewDossier.prevoir_demontage && <p>Prévoir le démontage : <strong>{viewDossier.prevoir_demontage}</strong></p>}
                          {viewDossier.prevoir_demontage === 'Oui' && viewDossier.date_demontage && <p>Date souhaitée : <strong>{fmtDate(viewDossier.date_demontage)}</strong></p>}
                        </div>
                      )}
                      {viewDossier.remplace_corner === 'Concurrent' && viewDossier.concurrent_demontage && (
                        <div className="ml-4 text-xs text-slate-600">
                          <p>Le concurrent prévoit le démontage : <strong>{viewDossier.concurrent_demontage}</strong></p>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Legacy: ancien format démontage */}
                  {!viewDossier.remplace_corner && viewDossier.demontage_ancien && (
                    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/50 text-sm">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-slate-500">Démontage ancien corner :</span>
                        <Badge variant={viewDossier.demontage_ancien === 'Oui' ? 'default' : 'secondary'}>{viewDossier.demontage_ancien}</Badge>
                      </div>
                    </div>
                  )}

                  {viewDossier.response_comment && (
                    <div className={`p-3 rounded-lg border ${viewDossier.status === 'valide_agenceur' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <p className="text-sm font-medium mb-1">{viewDossier.status === 'valide_agenceur' ? 'Validé' : 'Refusé'} par l'agenceur</p>
                      <p className="text-sm text-slate-600">{viewDossier.response_comment}</p>
                      {viewDossier.response_date_modifiee && <p className="text-sm mt-1 text-orange-600 font-medium">Nouvelle date: {fmtDate(viewDossier.response_date_modifiee)}</p>}
                    </div>
                  )}

                  {/* Notes / Remarques saisies à la commande */}
                  {viewDossier.notes && viewDossier.notes.trim() && (
                    <div className="p-3 rounded-lg border border-amber-300 bg-amber-50" data-testid="dossier-notes-block">
                      <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1.5">Notes / Remarques</p>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewDossier.notes}</p>
                    </div>
                  )}

                  {/* Files */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-slate-700">Fichiers ({(viewDossier.files || []).length})</p>
                      {['brouillon', 'refuse_agenceur'].includes(viewDossier.status) && (
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" onChange={e => handleFileUpload(e, viewDossier.id)} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png" />
                          <Button variant="outline" size="sm" className="gap-1 text-orange-600 pointer-events-none" disabled={uploading} data-testid="upload-file-btn">
                            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Ajouter
                          </Button>
                        </label>
                      )}
                    </div>
                    {(viewDossier.files || []).map(f => (
                      <div key={f.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg mb-1">
                        <div className="flex items-center gap-2 text-sm">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="truncate">{f.original_filename}</span>
                          <span className="text-xs text-slate-400">{(f.size / 1024).toFixed(0)} KB</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* History - eurodated */}
                  {(viewDossier.history || []).length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Historique</p>
                      <div className="space-y-1.5 max-h-52 overflow-y-auto">
                        {[...(viewDossier.history || [])].reverse().map((h, i) => {
                          const isResponse = h.action?.includes('reponse');
                          const isSend = h.action === 'envoi';
                          const isRelance = h.action?.includes('relance');
                          return (
                            <div key={i} className={`flex items-start gap-3 text-xs p-2.5 rounded-lg border ${
                              isResponse ? (h.action.includes('valide') ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200') :
                              isSend ? 'bg-blue-50 border-blue-200' :
                              isRelance ? 'bg-orange-50 border-orange-200' :
                              'bg-slate-50 border-slate-100'
                            }`}>
                              <div className="shrink-0 text-slate-500 font-mono whitespace-nowrap">{fmtDateTime(h.timestamp)}</div>
                              <div className="flex-1">
                                <span className="font-medium text-slate-700">{h.user_name || 'Système'}</span>
                                <span className="text-slate-500 ml-1">— {h.details}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter className="gap-2">
                  {viewDossier.status === 'brouillon' && (
                    <Button onClick={() => { handleSend(viewDossier.id); setViewDossier(null); }}
                      className="bg-green-600 hover:bg-green-700 text-white gap-1" data-testid="send-from-view">
                      <Send className="w-4 h-4" /> Envoyer
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-dossier-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2"><Trash2 className="w-5 h-5" />Supprimer</DialogTitle>
          </DialogHeader>
          {deleteTarget && <p className="text-sm text-slate-600">Supprimer <strong>{deleteTarget.enseigne} — {deleteTarget.ville}</strong> ?</p>}
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Date Change Modal */}
      <Dialog open={!!showDateChange} onOpenChange={v => { if (!v) setShowDateChange(null); }}>
        <DialogContent className="max-w-md" data-testid="date-change-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <Calendar className="w-5 h-5" />
              Modifier la date de mise en place
            </DialogTitle>
          </DialogHeader>
          {showDateChange && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
                <p><strong>Projet :</strong> {showDateChange.enseigne} — {showDateChange.ville}</p>
                <p><strong>Agenceur :</strong> {showDateChange.agenceur_name || showDateChange.agenceur_email}</p>
                <p><strong>Date actuelle :</strong> {showDateChange.date_previsionnelle ? new Date(showDateChange.date_previsionnelle).toLocaleDateString('fr-FR') : '—'}</p>
                <p><strong>Statut :</strong> {showDateChange.status === 'valide_agenceur' ? <Badge className="bg-green-100 text-green-700">Validé</Badge> : <Badge variant="secondary">{showDateChange.status}</Badge>}</p>
              </div>
              {showDateChange.status === 'valide_agenceur' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">Ce dossier a été validé par le prestataire. Un <strong>email d'alerte</strong> lui sera envoyé pour qu'il accepte ou refuse la nouvelle date.</p>
                </div>
              )}
              <div>
                <Label>Nouvelle date *</Label>
                <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} data-testid="new-date-input" />
              </div>
              <div>
                <Label>Raison de la modification</Label>
                <Input value={dateReason} onChange={e => setDateReason(e.target.value)} placeholder="Ex: Report demandé par le magasin" data-testid="date-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDateChange(null)}>Annuler</Button>
            <Button onClick={handleChangeDate} disabled={changingDate || !newDate} className="bg-amber-600 hover:bg-amber-700 gap-1" data-testid="confirm-date-change">
              {changingDate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Modifier la date
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aperçu Email modal */}
      <Dialog open={!!previewDossier || loadingPreview} onOpenChange={(o) => { if (!o && !confirmingSend) { setPreviewDossier(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="email-preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              Aperçu de l'email avant envoi
            </DialogTitle>
          </DialogHeader>
          {loadingPreview && !previewDossier && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          )}
          {previewDossier && (
            <div className="flex-1 overflow-y-auto space-y-3">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm space-y-1">
                <p><span className="text-slate-500">Destinataire :</span> <span className="font-medium">{previewDossier.to}</span></p>
                <p><span className="text-slate-500">Sujet :</span> <span className="font-medium">{previewDossier.subject}</span></p>
                {previewDossier.files && previewDossier.files.length > 0 && (
                  <p><span className="text-slate-500">Pièces jointes :</span> <span className="font-medium">{previewDossier.files.length} fichier(s)</span></p>
                )}
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white" style={{ maxHeight: '50vh' }}>
                <iframe
                  title="Aperçu email"
                  srcDoc={previewDossier.html}
                  sandbox=""
                  className="w-full"
                  style={{ height: '50vh', border: 'none' }}
                  data-testid="email-preview-iframe"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDossier(null)} disabled={confirmingSend}>
              Fermer
            </Button>
            <Button
              onClick={confirmSendFromPreview}
              disabled={confirmingSend || !previewDossier}
              className="bg-blue-600 hover:bg-blue-700 gap-1.5"
              data-testid="confirm-send-email-btn"
            >
              {confirmingSend ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Confirmer l'envoi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
